import {IApiServiceHandler} from "../api/apiServiceHandler";


export interface IAuthenticationHandler {
    authenticate: (username: string, password: string) => Promise<Response>;
    reauthenticate: () => Promise<Response | boolean>;
    apiService: IApiServiceHandler | null;
}

export class AuthenticationHandler implements IAuthenticationHandler {
    private readonly baseURL: string;
    private readonly accessTokenKey: string;
    private readonly refreshTokenKey: string;
    private readonly refreshTokenEndPoint: string;
    private readonly accessTokenEndPoint: string;
    private readonly authorizationHeader: string;
    private readonly authorizationTypePrefix: string;
    private _apiService: IApiServiceHandler | null;
    private readonly retryAuthenticate: boolean;

    constructor(
        baseURL: string,
        accessTokenEndPoint: string,
        refreshTokenEndPoint: string,
        accessTokenKey: string = "access",
        refreshTokenKey: string = "refresh",
        apiService: IApiServiceHandler | null = null,
        authorizationHeader: string = "Authorization",
        authorizationTypePrefix: string = "Bearer",
        retryAuthenticate: boolean = true
    ) {
        this.baseURL = baseURL;
        this.accessTokenKey = accessTokenKey;
        this.refreshTokenKey = refreshTokenKey;
        this.refreshTokenEndPoint = refreshTokenEndPoint;
        this.accessTokenEndPoint = accessTokenEndPoint;
        this.apiService = apiService;
        this._apiService = null;
        this.authorizationHeader = authorizationHeader;
        this.authorizationTypePrefix = authorizationTypePrefix;
        this.retryAuthenticate = retryAuthenticate;
    }

    get apiService(): IApiServiceHandler | null {
        return this._apiService;
    }

    set apiService(apiService: IApiServiceHandler | null) {
        this._apiService = apiService;
        if (apiService) {
            this.setAccessTokenInHeaderFromStore().then();
        }
    }

    async authenticate(username: string, password: string): Promise<Response> {
        const url = this.getAccessTokenUrl();
        const body = {username, password};
        const config: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        };
        const response = await fetch(url, config);
        const responseJson = await response.json();
        const accessToken = responseJson.access;
        const refreshToken = responseJson.refresh;
        localStorage.setItem(this.accessTokenKey, accessToken);
        localStorage.setItem(this.refreshTokenKey, refreshToken);
        await this.setAccessTokenInHeaderFromStore();
        return responseJson;
    }

    async reauthenticate(): Promise<Response | boolean> {
        if (!this.retryAuthenticate) {
            return false;
        }
        const url = this.getRefreshTokenUrl();
        const refreshToken = this.getRefreshToken();

        if (refreshToken && refreshToken !== "null") {
            const body = {refresh: refreshToken};
            const config: RequestInit = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            };

            const response = await fetch(url, config);
            const responseJson = await response.json();

            const accessToken = responseJson.access;
            await this.setAccessTokenInHeader(accessToken);
            return response;
        } else {
            await this.setAccessTokenInHeader(null);
        }
        return false;
    }

    async setAccessTokenInHeaderFromStore(): Promise<void> {
        const accessToken = localStorage.getItem(this.accessTokenKey);
        await this.setAccessTokenInHeader(accessToken);
    }

    async setAccessTokenInHeader(accessToken: string | null): Promise<void> {
        if (accessToken && this.apiService) {
            let prefixAccessToken = `${this.authorizationTypePrefix} ${accessToken}`;
            prefixAccessToken.trim();
            this.apiService.headers.set(this.authorizationHeader, prefixAccessToken);
            localStorage.setItem(this.accessTokenKey, accessToken);
        } else {
            this.apiService?.headers.delete(this.authorizationHeader);
            localStorage.removeItem(this.accessTokenKey);
        }
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    getRefreshTokenUrl(): string {
        return this.refreshTokenEndPoint.includes(this.baseURL)
            ? this.refreshTokenEndPoint
            : this.baseURL + this.refreshTokenEndPoint;
    }

    getAccessTokenUrl(): string {
        return this.accessTokenEndPoint.includes(this.baseURL)
            ? this.accessTokenEndPoint
            : this.baseURL + this.accessTokenEndPoint;
    }
}
